import { useEffect, useRef, useCallback } from 'react';

const INACTIVITY_TIMEOUT = 20 * 60 * 1000; // 20 minutos
const STORAGE_KEY = 'vital_last_activity';
const EVENTS = ['click', 'touchstart', 'scroll', 'keydown', 'mousemove'];

/**
 * Hook de logout automático por inatividade (20 min).
 * - Monitora: click, touchstart, scroll, keydown, mousemove
 * - Sincroniza entre abas via localStorage event
 * - Limpa tokens e redireciona para /
 */
export function useInactivityLogout(onLogout) {
  const timerRef = useRef(null);

  const updateActivity = useCallback(() => {
    const now = Date.now().toString();
    try {
      localStorage.setItem(STORAGE_KEY, now);
    } catch { /* ignore quota errors */ }
  }, []);

  const startTimer = useCallback(() => {
    if (timerRef.current) clearTimeout(timerRef.current);
    timerRef.current = setTimeout(() => {
      // Verificar se outra aba atualizou a atividade
      try {
        const last = parseInt(localStorage.getItem(STORAGE_KEY) || '0', 10);
        const elapsed = Date.now() - last;
        if (elapsed < INACTIVITY_TIMEOUT) {
          // Outra aba teve atividade recente, reiniciar timer
          startTimer();
          return;
        }
      } catch { /* ignore */ }
      // Timeout real: executar logout
      onLogout();
    }, INACTIVITY_TIMEOUT);
  }, [onLogout]);

  const handleActivity = useCallback(() => {
    updateActivity();
    startTimer();
  }, [updateActivity, startTimer]);

  useEffect(() => {
    // Inicializar
    updateActivity();
    startTimer();

    // Escutar eventos de interação
    EVENTS.forEach(event => {
      window.addEventListener(event, handleActivity, { passive: true });
    });

    // Sincronizar entre abas via storage event
    const handleStorage = (e) => {
      if (e.key === STORAGE_KEY && e.newValue) {
        startTimer(); // Outra aba teve atividade, resetar timer
      }
    };
    window.addEventListener('storage', handleStorage);

    return () => {
      if (timerRef.current) clearTimeout(timerRef.current);
      EVENTS.forEach(event => {
        window.removeEventListener(event, handleActivity);
      });
      window.removeEventListener('storage', handleStorage);
    };
  }, [handleActivity, startTimer, updateActivity]);
}
